/**
 * Raise a credit note, in the invoice detail's layout:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/create-invoice.tsx
 *   a document form: the lines on the left, what is being said about them beside it;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid.
 *
 * Always raised against a named invoice — `?invoiceId=` is required. Computes no credit amount: the
 * returned quantity is the user's raw entry, and the taxable value/tax/total come back from the API
 * on success. The line table is a plain table, not a paged one: paging would unmount the inputs on
 * the pages not shown.
 */
import { useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import {
  PiCalendarBlankDuotone as CalendarIcon,
  PiChatTextDuotone as ReasonIcon,
  PiFileTextDuotone as NoteIcon,
  PiInfoDuotone as InfoIcon,
  PiListChecksDuotone as LinesIcon,
  PiReceiptDuotone as ReceiptIcon,
} from "react-icons/pi";
import { Avatar, Badge, Button, Input, Table, Text, Textarea } from "rizzui";
import { GRID_TABLE } from "../../components/table/table-view";
import { Link, useLoaderData, useNavigate, type LoaderFunctionArgs } from "react-router-dom";
import { EmptyState, FieldError, WidgetCard } from "@steelyard/theme";
import { CardTitle, PageHero, StatusPill } from "../../components/detail-parts";
import { formatDate } from "../../lib/format";
import { useGoBack } from "../../lib/navigation";
import { getInvoice, issueCreditNote, money, type CreditNoteLine } from "../../lib/services/invoicing";
import { creditNotePath, invoicePath } from "../../lib/paths";

/** `?invoiceId=` preselects the invoice by id — a form parameter, not the record's URL, so it stays an id. */
export async function creditNotesLoader({ request }: LoaderFunctionArgs) {
  const invoiceId = new URL(request.url).searchParams.get("invoiceId");
  const invoice = invoiceId ? await getInvoice(invoiceId) : null;
  return { invoice, invoiceId };
}

const AVATAR_COLORS = ["secondary", "info", "primary"] as const;

export default function CreditNotesPage() {
  const { invoice: data, invoiceId } = useLoaderData() as Awaited<ReturnType<typeof creditNotesLoader>>;
  const navigate = useNavigate();
  const goBack = useGoBack(data ? invoicePath(data.invoiceNumber) : "/invoicing");
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  if (!invoiceId || !data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHero
          breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: "Credit Note" }]}
          icon={<NoteIcon aria-hidden />}
          iconClassName="bg-orange"
          title="Credit Note"
          description="A credit note is always raised against one invoice."
        />
        <WidgetCard rounded="xl">
          <EmptyState
            title="Open the invoice first"
            icon={<NoteIcon aria-hidden className="size-5" />}
            action={
              <Link to="/invoicing">
                <Button as="span" size="sm">
                  Go to the invoice list
                </Button>
              </Link>
            }
          >
            A credit note is raised against a named invoice and inherits its lines. Open the invoice and raise the credit note from there.
          </EmptyState>
        </WidgetCard>
      </div>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(undefined);

    const formData = new FormData(event.currentTarget);
    const reason = String(formData.get("reason") ?? "").trim();
    if (reason === "") return setError("A reason is required.");

    const lines: CreditNoteLine[] = [...formData.entries()]
      .filter(([key]) => key.startsWith("line:"))
      .map(([key, value]) => ({ invoiceLineId: key.slice("line:".length), quantity: String(value ?? "").trim() }))
      .filter((l) => l.quantity !== "" && Number(l.quantity) !== 0);

    if (lines.some((l) => !Number.isFinite(Number(l.quantity)) || Number(l.quantity) < 0)) {
      return setError("A returned quantity must be a number, and cannot be negative.");
    }
    if (lines.length === 0) return setError("Enter the returned quantity on at least one line.");

    setPending(true);
    try {
      const creditNoteKey = await issueCreditNote(invoiceId!, reason, lines);
      toast.success("Credit note raised");
      // On to the new note itself (its compliance statuses and print), in place of this form, so
      // that page's Back returns to wherever this form was opened from.
      navigate(creditNotePath(creditNoteKey), { replace: true });
    } catch (err) {
      const message = (err as { detail?: string; title?: string }).detail ?? (err as { title?: string }).title ?? "The credit note was refused.";
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: data.invoiceNumber, href: invoicePath(data.invoiceNumber) }, { name: "Credit Note" }]}
        back={{ href: invoicePath(data.invoiceNumber), label: "Back to the invoice" }}
        icon={<NoteIcon aria-hidden />}
        iconClassName="bg-orange"
        title="Raise a Credit Note"
        description={`Against invoice ${data.invoiceNumber}, for ${data.recipientLegalName}.`}
        meta={
          <>
            {/* §13.5 [D-23]: a credit note is its own GST document raised against an invoice, not a negative invoice. */}
            <Badge variant="flat" color="warning" rounded="pill">
              Not an invoice
            </Badge>
            <StatusPill status={data.state} />
            <Badge variant="outline" rounded="pill" className="gap-1.5 border-muted text-gray-700">
              <ReceiptIcon aria-hidden className="size-4" />
              {data.invoiceNumber} · {money(data.grandTotal)}
            </Badge>
            <Text as="span" className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <CalendarIcon aria-hidden className="size-4" />
              Invoiced {formatDate(data.invoiceDate)}
            </Text>
          </>
        }
      />

      <form onSubmit={handleSubmit} noValidate aria-busy={pending} className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <WidgetCard
          rounded="xl"
          title={
            <CardTitle icon={<LinesIcon aria-hidden className="size-5" />} tile="bg-secondary-lighter text-secondary-dark">
              <span className="flex items-center gap-2">
                Returned lines
                <Badge variant="flat" color="secondary" rounded="pill" size="sm">
                  {data.lines.length}
                </Badge>
              </span>
            </CardTitle>
          }
          className="overflow-hidden p-0 lg:p-0 @5xl:col-span-8"
          headerClassName="items-center px-5 pb-4 pt-5 lg:px-7 lg:pt-6"
        >
          <div className="overflow-x-auto">
            <Table variant="modern" className={`w-full min-w-[640px] ${GRID_TABLE}`}>
              <caption className="sr-only">Invoice lines with the quantity invoiced and the quantity being returned</caption>
              <Table.Header>
                <Table.Row className="hover:bg-transparent">
                  <Table.Head className="ps-5 lg:ps-7">Product</Table.Head>
                  <Table.Head className="text-right">Invoiced</Table.Head>
                  <Table.Head className="text-right">Line total</Table.Head>
                  <Table.Head className="pe-5 text-right lg:pe-7">Returned qty</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.lines.map((line, index) => (
                  <Table.Row key={line.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-100/40">
                    <Table.Cell className="py-4 ps-5 lg:ps-7">
                      <div className="flex items-center gap-3">
                        <Avatar name={line.skuName} color={AVATAR_COLORS[index % AVATAR_COLORS.length]} rounded="md" size="md" />
                        <div className="min-w-0">
                          <Text className="line-clamp-2 font-medium text-gray-900">{line.skuName}</Text>
                          <Text className="text-xs text-gray-500">{line.brandName}</Text>
                          <Badge variant="outline" size="sm" rounded="md" className="mt-1 border-muted font-mono text-[11px] text-gray-600">
                            HSN {line.hsnCode}
                          </Badge>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-4 text-right">
                      <span className="font-medium tabular-nums text-gray-900">
                        {String(line.quantity)} <span className="text-xs text-gray-500">{line.unitCode}</span>
                      </span>
                    </Table.Cell>
                    <Table.Cell className="py-4 text-right tabular-nums text-gray-700">
                      {line.lineTotalDisplay}
                      {line.discountPercent ? <span className="block text-xs text-green-dark">after {String(line.discountPercent)}% discount</span> : null}
                    </Table.Cell>
                    <Table.Cell className="py-4 pe-5 lg:pe-7">
                      <div className="flex justify-end">
                        <Input
                          id={`line-${line.id}`}
                          name={`line:${line.id}`}
                          aria-label={`Returned quantity for ${line.skuName}`}
                          type="number"
                          min="0"
                          step="any"
                          inputMode="decimal"
                          defaultValue=""
                          placeholder="0"
                          size="sm"
                          suffix={<span className="text-xs text-gray-500">{line.unitCode}</span>}
                          className="w-36"
                          inputClassName="text-right font-medium"
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <div className="flex items-center gap-2 border-t border-muted bg-gray-50 px-5 py-3.5 text-sm text-gray-600 lg:px-7 dark:bg-gray-100/40">
            <InfoIcon aria-hidden className="size-4 shrink-0 text-secondary" />
            Leave a line blank to keep it off the credit note. Taxable value, tax and total come back from Billing once it is raised
            {data.discount ? " — at the discounted price this invoice charged, not the undiscounted rate" : ""}.
          </div>
        </WidgetCard>

        <WidgetCard
          rounded="xl"
          title={
            <CardTitle icon={<ReasonIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
              Reason
            </CardTitle>
          }
          headerClassName="items-center"
          className="@5xl:col-span-4"
        >
          <div className="mt-5 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Textarea
                id="credit-note-reason"
                name="reason"
                label="Reason"
                required
                rows={4}
                aria-describedby="credit-note-reason-hint"
                aria-invalid={error ? true : undefined}
              />
              <Text id="credit-note-reason-hint" className="text-xs text-gray-500">
                Why the goods came back. Recorded immutably against both documents.
              </Text>
              {error ? <FieldError message={error} /> : null}
            </div>

            <div className="rounded-lg border border-s-4 border-muted border-s-secondary bg-secondary-lighter/30 px-4 py-3 text-sm text-gray-700 dark:bg-secondary-lighter/10">
              It then carries <strong className="font-semibold text-gray-900">its own compliance statuses</strong> — IRN, e-way bill and Tally —
              tracked exactly as an invoice’s are. Raising it also posts a credit-note voucher to Tally against the invoice’s voucher.
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-muted pt-4 @xs:flex-row @xs:justify-end">
              <Button type="button" variant="outline" onClick={goBack}>
                Cancel
              </Button>
              <Button type="submit" isLoading={pending}>
                <NoteIcon aria-hidden className="me-1.5 size-4" />
                {pending ? "Raising…" : "Raise the credit note"}
              </Button>
            </div>
          </div>
        </WidgetCard>
      </form>
    </div>
  );
}
